import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { isStaleChunkError, reloadForStaleChunk } from '../../utils/staleChunkReload';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  isReloading?: boolean;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child
 * component tree and displays a fallback UI instead of crashing the whole app.
 * 
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 * 
 * With custom fallback:
 * ```tsx
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // A lazy-loaded chunk failed to fetch — almost always a stale session
    // after a new deploy (old hashed chunks no longer exist on the server).
    // Reload to pick up the new build instead of showing the error screen.
    if (isStaleChunkError(error) && reloadForStaleChunk()) {
      this.setState({ errorInfo, isReloading: true });
      return;
    }

    this.setState({ errorInfo });
    
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);
    
    // Call optional error handler (for error reporting services like Sentry)
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    // Page reload is in flight (stale chunk after a deploy) — render a quiet
    // blank screen for the split second before the browser navigates.
    if (this.state.isReloading) {
      return null;
    }

    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gradient-to-br from-ink-50 to-ink-100 flex items-center justify-center p-6">
          <div className="max-w-md w-full">
            <div className="bg-surface rounded-2xl shadow-xl border border-ink-100 overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-danger-500 to-danger-600 px-6 py-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                  <AlertTriangle className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-xl font-semibold text-white">
                  Something went wrong
                </h1>
                <p className="text-danger-100 mt-2 text-sm">
                  We encountered an unexpected error
                </p>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <p className="text-ink-600 text-sm text-center">
                  Don't worry, your data is safe. Try refreshing the page or going back to the home screen.
                </p>

                {/* Error details (development only) */}
                {import.meta.env.DEV && this.state.error && (
                  <details className="mt-4">
                    <summary className="text-xs text-ink-400 cursor-pointer hover:text-ink-600 transition-colors">
                      Technical details
                    </summary>
                    <div className="mt-2 p-3 bg-ink-50 rounded-lg overflow-auto max-h-40">
                      <code className="text-xs text-danger-600 whitespace-pre-wrap break-all">
                        {this.state.error.message}
                        {this.state.errorInfo?.componentStack && (
                          <>
                            {'\n\nComponent Stack:'}
                            {this.state.errorInfo.componentStack}
                          </>
                        )}
                      </code>
                    </div>
                  </details>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={this.handleRetry}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-ink-900 rounded-xl hover:bg-ink-800 active:scale-[0.98] transition-all"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try again
                  </button>
                  <button
                    onClick={this.handleGoHome}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-ink-700 bg-surface border border-ink-200 rounded-xl hover:bg-ink-50 hover:border-ink-300 active:scale-[0.98] transition-all"
                  >
                    <Home className="w-4 h-4" />
                    Go home
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <p className="text-center text-xs text-ink-400 mt-4">
              If this problem persists, please contact support
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
